// ARCA-619 / ARCA-715 — composite chess-step control deck.
//
// Replaces the mutually-exclusive Phase-B MOVE / EMPOWER / POWER / PASS segment
// radiogroup. The chess step is now COMPOSITE: the player may stage ANY
// combination of an optional move + a role action (empower XOR extend) + a
// power-up, in any order, then Submit once (an empty Submit is the do-nothing
// step that replaces Pass — D4).
//
// This deck renders the two pieces of that surface that are NOT board gestures:
//   • the EMPOWER entry button (opens the role-first EmpowerModal flow), and
//   • the staged-chess SUMMARY — one chip per staged slot, each with a
//     per-slot Remove control (this replaces the old "discard staged action?"
//     confirm dialog: clearing a slot is now explicit and non-destructive to
//     the others).
// The move (board drag) and re-empower (tap an own empowered piece) gestures
// live on the board surfaces; a hint line points the player at them. The
// power-up tray + token bank are rendered alongside this deck by MatchScreen.

import type { JSX } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import type { PlayerView, RoleKind } from '../../game/PlayerViewSchema'
import type { PhaseBComposite } from '../../stores/turnBuilder'
import { hasStagedChessAction } from '../../stores/turnBuilder'
import { Btn } from '../components/Btn'

export interface ChessControlDeckProps {
  readonly phaseB: PhaseBComposite
  readonly playerView: PlayerView
  /**
   * Localized reason the empower entry is unplayable this turn (no word
   * committed / no empowerable piece), or `null` when empower is playable. When
   * set, the Empower button renders disabled with the reason as its accessible
   * description.
   */
  readonly empowerDisabledReason: string | null
  /**
   * ARCA-769 — true when a move and an empower are staged on the SAME piece and
   * the move is illegal under the newly-empowered role (computed by MatchScreen
   * via `stagedMoveEmpowerConflict`). When set, the staged-move chip is flagged
   * and a warning + Discard-move action render below the summary; MatchScreen
   * also blocks Submit so the `illegal_move` combination never round-trips.
   */
  readonly moveConflict: boolean
  readonly onEmpower: () => void
  readonly onClearMove: () => void
  /** Clears the role slot (empower XOR extend — only one is ever staged). */
  readonly onClearRole: () => void
  readonly onClearPowerup: () => void
}

/** A single staged-slot chip with a per-slot Remove (✕) control. When
 *  `conflict` is set the chip border switches to the destructive token so the
 *  staged-move chip reads as needing attention (ARCA-769). */
function StagedChip({
  slot,
  label,
  removeLabel,
  onClear,
  conflict = false,
}: {
  slot: string
  label: JSX.Element | string
  removeLabel: string
  onClear: () => void
  conflict?: boolean
}): JSX.Element {
  return (
    <li
      data-staged-slot={slot}
      {...(conflict ? { 'data-conflict': 'true' } : {})}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 6px 4px 12px',
        borderRadius: 'var(--radius-pill)',
        background: 'var(--color-surface-mid)',
        border: `1px solid ${
          conflict ? 'var(--color-cta-destructive)' : 'var(--color-cta-primary)'
        }`,
        color: 'var(--color-text-primary)',
        fontFamily: 'var(--font-family-display)',
        fontWeight: 700,
        fontSize: 'var(--font-size-13)',
      }}
    >
      <span>{label}</span>
      <button
        type="button"
        data-clear-slot={slot}
        aria-label={removeLabel}
        onClick={onClear}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 22,
          height: 22,
          padding: 0,
          borderRadius: 'var(--radius-pill)',
          border: 'none',
          background: 'var(--color-surface-deep)',
          color: 'var(--color-text-secondary)',
          fontSize: 'var(--font-size-14)',
          lineHeight: 1,
          cursor: 'pointer',
        }}
      >
        {'×'}
      </button>
    </li>
  )
}

export function ChessControlDeck({
  phaseB,
  playerView,
  empowerDisabledReason,
  moveConflict,
  onEmpower,
  onClearMove,
  onClearRole,
  onClearPowerup,
}: ChessControlDeckProps): JSX.Element {
  const intl = useIntl()
  const anyStaged = hasStagedChessAction(phaseB)

  // ARCA-769 — the conflict warning is meaningful only while BOTH the move and
  // the empower remain staged; clearing either resolves it. (MatchScreen only
  // sets `moveConflict` when both are staged on the same piece, but re-derive
  // the render guard locally so a stale prop can never paint an orphan warning.)
  const moveConflictActive =
    moveConflict && phaseB.move !== null && phaseB.empower !== null

  const roleLabel = (role: RoleKind | null): string =>
    role !== null && role !== 'king'
      ? intl.formatMessage({ id: `match.empower.role.${role}` })
      : intl.formatMessage({ id: 'match.chess.staged.role_fallback' })

  // Re-empower (extend) keeps the piece's existing role; resolve it from the
  // board for the chip label.
  const extendRole =
    phaseB.extend !== null
      ? (playerView.board.find((c) => c.cell_idx === phaseB.extend?.cell_idx)
          ?.role_kind ?? null)
      : null

  const empowerDisabled = empowerDisabledReason !== null

  return (
    <div
      data-screen-region="chess-control-deck"
      style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
    >
      {anyStaged ? (
        <ul
          data-screen-region="staged-chess-summary"
          aria-label={intl.formatMessage({ id: 'match.chess.staged.heading' })}
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            justifyContent: 'center',
          }}
        >
          {phaseB.move !== null ? (
            <StagedChip
              slot="move"
              conflict={moveConflictActive}
              label={<FormattedMessage id="match.chess.staged.move" />}
              removeLabel={intl.formatMessage(
                { id: 'match.chess.staged.clear' },
                {
                  action: intl.formatMessage({ id: 'match.chess.staged.move' }),
                },
              )}
              onClear={onClearMove}
            />
          ) : null}
          {phaseB.empower !== null ? (
            <StagedChip
              slot="empower"
              label={intl.formatMessage(
                { id: 'match.chess.staged.empower' },
                { role: roleLabel(phaseB.empower.role) },
              )}
              removeLabel={intl.formatMessage(
                { id: 'match.chess.staged.clear' },
                {
                  action: intl.formatMessage(
                    { id: 'match.chess.staged.empower' },
                    { role: roleLabel(phaseB.empower.role) },
                  ),
                },
              )}
              onClear={onClearRole}
            />
          ) : null}
          {phaseB.extend !== null ? (
            <StagedChip
              slot="extend"
              label={intl.formatMessage(
                { id: 'match.chess.staged.extend' },
                { role: roleLabel(extendRole) },
              )}
              removeLabel={intl.formatMessage(
                { id: 'match.chess.staged.clear' },
                {
                  action: intl.formatMessage(
                    { id: 'match.chess.staged.extend' },
                    { role: roleLabel(extendRole) },
                  ),
                },
              )}
              onClear={onClearRole}
            />
          ) : null}
          {phaseB.powerup !== null ? (
            <StagedChip
              slot="powerup"
              label={intl.formatMessage({
                id: `match.powerup.name.${phaseB.powerup.name}`,
              })}
              removeLabel={intl.formatMessage(
                { id: 'match.chess.staged.clear' },
                {
                  action: intl.formatMessage({
                    id: `match.powerup.name.${phaseB.powerup.name}`,
                  }),
                },
              )}
              onClear={onClearPowerup}
            />
          ) : null}
        </ul>
      ) : (
        <p
          data-screen-region="chess-empty-hint"
          style={{
            margin: 0,
            textAlign: 'center',
            color: 'var(--color-text-secondary)',
            fontFamily: 'var(--font-family-body)',
            fontSize: 'var(--font-size-13)',
          }}
        >
          <FormattedMessage id="match.chess.empty_hint" />
        </p>
      )}

      {/* ARCA-769 — same-piece empower+move conflict. The canonical apply order
          (powerup → empower → move) validates the move under the NEW role, so an
          empower that makes the staged move illegal would 409 on Submit. Warn +
          offer Discard MOVE (reusing onClearMove) and block Submit (MatchScreen)
          until the player removes the move or the empower. */}
      {moveConflictActive && phaseB.empower !== null ? (
        <div
          data-screen-region="move-conflict-warning"
          role="alert"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 8,
            padding: '8px 12px',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-cta-destructive)',
            background: 'var(--color-surface-mid)',
          }}
        >
          <p
            style={{
              margin: 0,
              textAlign: 'center',
              color: 'var(--color-text-primary)',
              fontFamily: 'var(--font-family-body)',
              fontSize: 'var(--font-size-13)',
            }}
          >
            <FormattedMessage
              id="match.chess.conflict.move_illegal"
              values={{ role: roleLabel(phaseB.empower.role) }}
            />
          </p>
          <Btn
            kind="ghost"
            size="sm"
            testId="discard-move-btn"
            onClick={onClearMove}
          >
            <FormattedMessage id="match.chess.conflict.discard_move" />
          </Btn>
        </div>
      ) : null}

      <Btn
        kind="gold"
        size="md"
        block
        testId="chess-empower-btn"
        disabled={empowerDisabled}
        onClick={onEmpower}
        {...(empowerDisabled && empowerDisabledReason !== null
          ? { ariaLabel: empowerDisabledReason }
          : {})}
      >
        <FormattedMessage id="match.chess.empower_btn" />
      </Btn>
      {empowerDisabled && empowerDisabledReason !== null ? (
        <p
          data-screen-region="empower-disabled-reason"
          style={{
            margin: 0,
            textAlign: 'center',
            color: 'var(--color-text-secondary)',
            fontFamily: 'var(--font-family-body)',
            fontSize: 'var(--font-size-12)',
          }}
        >
          {empowerDisabledReason}
        </p>
      ) : null}

      <p
        style={{
          margin: 0,
          textAlign: 'center',
          color: 'var(--color-text-secondary)',
          fontFamily: 'var(--font-family-body)',
          fontSize: 'var(--font-size-12)',
        }}
      >
        <FormattedMessage id="match.chess.hint" />
      </p>
    </div>
  )
}
